import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle, CheckCircle2, Megaphone, Users, BookOpen } from "lucide-react";
import { Pill, Button } from "../../ui";
import { toast } from "sonner";

const PRIORITY_CFG = {
  normal:    { label: "Normal",    barCls: "bg-slate-300",      pillBg: "#F1F5F9", pillCol: "#475569" },
  important: { label: "Important", barCls: "bg-brand-400",      pillBg: "#FEF0E6", pillCol: "#E8690A" },
  urgent:    { label: "Urgent",    barCls: "bg-accent-red",     pillBg: "#FDECEA", pillCol: "#C0392B" },
};

const TARGET_ICON = { all: Megaphone, class: BookOpen, teacher: Users };

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function targetLabel(a) {
  if (a.targetType === "all") return "All Staff";
  if (a.targetType === "class") return `Classes: ${(a.targetIds || []).join(", ")}`;
  if (a.targetType === "teacher") return `Teachers (${(a.targetIds || []).length})`;
  return a.targetType || "";
}

export default function AnnouncementModal({ announcement, onClose, onAcknowledge }) {
  const [localAck, setLocalAck] = useState(false);
  if (!announcement) return null;

  const cfg = PRIORITY_CFG[announcement.priority] || PRIORITY_CFG.normal;
  const needsAck   = announcement.requiresAck && !localAck;
  const TargetIcon = TARGET_ICON[announcement.targetType] || Megaphone;

  const handleClose = () => {
    if (needsAck) {
      toast.warning("Please acknowledge this announcement before closing.", { duration: 3000 });
      return;
    }
    onClose();
  };

  const handleAcknowledge = () => {
    setLocalAck(true);
    onAcknowledge();
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[1200] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop — blocked when requiresAck and not yet acked */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={needsAck ? () => toast.warning("Please acknowledge before closing.") : onClose}
        />

        <motion.div
          className="relative bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          style={{ width: "min(560px, 95vw)", maxHeight: "90vh" }}
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 24 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          {/* Priority accent bar */}
          <div className={`h-1.5 w-full ${cfg.barCls} shrink-0`} />

          {/* Header */}
          <div className="px-7 pt-5 pb-4 flex items-start justify-between gap-4 border-b border-navy-50">
            <div className="flex flex-col gap-2 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Pill bg={cfg.pillBg} col={cfg.pillCol}>{cfg.label}</Pill>
                {announcement.requiresAck && (
                  <Pill bg="#F5F3FF" col="#7C3AED" className="!gap-1">
                    <AlertTriangle size={11} strokeWidth={2.5} />
                    Acknowledgment Required
                  </Pill>
                )}
              </div>
              <h2 className="font-sora font-bold text-[20px] text-navy-800 leading-snug">
                {announcement.title}
              </h2>
            </div>

            <button
              onClick={handleClose}
              className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border-none transition-all duration-200 mt-0.5 ${
                needsAck
                  ? "bg-navy-50 text-navy-300 cursor-not-allowed"
                  : "bg-navy-50 text-navy-500 hover:bg-navy-100 hover:text-navy-800 cursor-pointer"
              }`}
              title={needsAck ? "Acknowledge to close" : "Close"}
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>

          {/* Meta */}
          <div className="px-7 py-3 flex items-center gap-3 flex-wrap text-[13px] text-navy-400 bg-slate-50 border-b border-navy-50">
            <span className="font-semibold text-navy-600">
              {announcement.createdBy?.name || "System"}
            </span>
            <span>·</span>
            <span className="capitalize">{announcement.createdBy?.role || "Admin"}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <TargetIcon size={13} strokeWidth={2.5} />
              {targetLabel(announcement)}
            </span>
            <span>·</span>
            <span>{formatTime(announcement.createdAt)}</span>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-7 py-6">
            <p className="text-[15px] text-navy-700 leading-relaxed whitespace-pre-wrap">
              {announcement.body}
            </p>
          </div>

          {/* Acknowledgment notice */}
          {announcement.requiresAck && !localAck && (
            <div className="mx-7 mb-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3.5 flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" strokeWidth={2.5} />
              <div>
                <div className="font-sora font-bold text-[13px] text-amber-800 mb-0.5">
                  Acknowledgment Required
                </div>
                <div className="text-[12px] text-amber-700">
                  You must acknowledge this announcement before closing. This confirms you have read and understood the content.
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-7 pb-6 flex items-center justify-end gap-3 shrink-0">
            {!announcement.requiresAck && (
              <Button variant="ghost" onClick={onClose} small>
                Close
              </Button>
            )}
            {announcement.requiresAck && !localAck && (
              <>
                <Button variant="ghost" small onClick={handleClose}>
                  Close
                </Button>
                <Button
                  variant="orange"
                  onClick={handleAcknowledge}
                  className="gap-2"
                >
                  <CheckCircle2 size={17} strokeWidth={2.5} />
                  I Acknowledge
                </Button>
              </>
            )}
            {announcement.requiresAck && localAck && (
              <Button variant="green" onClick={onClose} className="gap-2">
                <CheckCircle2 size={17} strokeWidth={2.5} />
                Acknowledged — Close
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
